/**
 * DialerDataContext — Data layer (React Query) du dialer.
 *
 * 2026-06-04 (Fowler etape 2/5 du decoupage) — Extrait du god-context
 * `DialerContext.tsx` selon la carte des dependances
 * `docs/cartography/dialer-context-dependencies.md` §5.
 *
 * Responsabilite unique :
 *   "Quelles donnees prospects / appels sont chargees (et auto-rafraichies)
 *    pour le caller authentifie et le customer selectionne ?"
 *
 * Sources de verite :
 *   - prospectQueue      ← ProspectQueue.filter (poll 5s)
 *   - allCalls           ← ParallelCall.filter (poll 3s)
 *   - activeCalls        ← ParallelCall.filter(active_statuses) (poll 5s/10s)
 *                          + cleanup ringTimeout + stuck-call cleanup
 *   - selectedCustomerId ← localStorage 'xdial_selectedCustomerId'
 *
 * Dependances entrantes :
 *   - useDialerAuth() → callerData (scope ADR-024 : tenant + customer)
 *
 * Dependances injectees (props) :
 *   - ringTimeout       : settings, gere le cleanup auto des appels en sonnerie
 *   - dialerRunState    : ajuste la cadence de poll (5s en run, 10s sinon)
 *   - powerMode         : idem (poll plus agressif en Power Mode)
 *
 * Consommateurs (lecture) :
 *   - DialerContext.tsx (refactor en cours)
 *   - useDialerSessionHandlers (via props depuis DialerContext)
 *   - useDialerEvents (via queryClient.invalidateQueries)
 *
 * Pas de cycle : Auth → Data, et personne ne mute Auth depuis ici.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { POLLING } from '@/config/polling';
import { isStuckCall } from '@/services/dialer/stuckCall';
// @ts-ignore — api-client est en .js sans types
import { api } from '@/api/api-client';
import { useDialerAuth } from './DialerAuthContext';
import * as TwilioCalls from '@/services/dialer/TwilioCallService';

// ── Public shape ──────────────────────────────────────────────────────────────

export interface ParallelCall {
  id: string;
  call_status?: string;
  call_outcome?: string | null;
  external_call_id?: string;
  started_at?: string;
  created_date?: string;
  ended_at?: string;
  agent_email?: string;
  customer_id?: string;
  prospect_phone?: string;
  prospect_name?: string;
  prospect_company?: string;
  [key: string]: unknown;
}

export interface ProspectQueueItem {
  id: string;
  status?: string;
  prospect_phone?: string;
  prospect_name?: string;
  customer_id?: string;
  source?: string;
  do_not_call?: boolean;
  last_call_at?: string;
  [key: string]: unknown;
}

export interface DialerDataContextValue {
  // Customer scope (ADR-024)
  selectedCustomerId: string | null;
  setSelectedCustomerId: (id: string | null) => void;

  // Live data
  prospectQueue: ProspectQueueItem[];
  allCalls: ParallelCall[];
  activeCalls: ParallelCall[];
}

interface DialerDataProviderProps {
  children: React.ReactNode;
  /** Settings injectes pour la cadence de poll et le cleanup. */
  ringTimeout: number;
  dialerRunState: string;
  powerMode: boolean;
  /** ENG-4: the queue and history poll (false off the screens that show them, outside a session). */
  displayListsLive?: boolean;
}

const DialerDataContext = createContext<DialerDataContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

// Stable empty values while a query has no data. A `= []` default is a new
// array on every render, so every effect keyed on it ran on every render; the
// one that records dialled phones set state each time and looped ("Maximum
// update depth exceeded", 450 times per page for a viewer, RUI-17).
const EMPTY_QUEUE: ProspectQueueItem[] = [];
const EMPTY_CALLS: ParallelCall[] = [];

export function DialerDataProvider({
  children,
  ringTimeout,
  dialerRunState,
  powerMode,
  displayListsLive = true,
}: DialerDataProviderProps) {
  const { callerData } = useDialerAuth();

  // selectedCustomerId — ADR-024 phase 2 : scope = (tenant, customer).
  const [selectedCustomerId, _setSelectedCustomerId] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem('xdial_selectedCustomerId') || null;
      } catch {
        return null;
      }
    },
  );

  const setSelectedCustomerId = useCallback((id: string | null) => {
    _setSelectedCustomerId(id || null);
    try {
      if (id) localStorage.setItem('xdial_selectedCustomerId', id);
      else localStorage.removeItem('xdial_selectedCustomerId');
    } catch {}
  }, []);

  // ── Q1 : prospectQueue ───────────────────────────────────────────────────
  const { data: prospectQueue = EMPTY_QUEUE } = useQuery<ProspectQueueItem[]>({
    queryKey: ['prospect-queue', callerData?.id, selectedCustomerId],
    queryFn: () =>
      api.entities.ProspectQueue.filter<ProspectQueueItem>(
        selectedCustomerId ? { customer_id: selectedCustomerId } : {},
        '-created_date',
        1000,
      ),
    enabled: !!callerData,
    refetchInterval: displayListsLive ? POLLING.queueMs : false,
  });

  // ── Q2 : allCalls ────────────────────────────────────────────────────────
  const { data: allCalls = EMPTY_CALLS } = useQuery<ParallelCall[]>({
    queryKey: ['all-calls', callerData?.id, selectedCustomerId],
    queryFn: () =>
      api.entities.ParallelCall.filter(
        selectedCustomerId ? { customer_id: selectedCustomerId } : {},
        '-created_date',
        500,
      ),
    enabled: !!callerData,
    refetchInterval: displayListsLive ? POLLING.callsMs : false,
  });

  // ── Q3 : activeCalls (avec cleanup auto) ─────────────────────────────────
  const { data: activeCalls = EMPTY_CALLS } = useQuery<ParallelCall[]>({
    queryKey: [
      'parallel-calls',
      callerData?.id,
      selectedCustomerId,
      callerData?.caller_email,
    ],
    queryFn: async () => {
      const baseFilter: Record<string, unknown> = {
        agent_email: callerData?.caller_email,
        call_status: {
          $in: [
            'dialing',
            'ringing',
            'ai_analyzing',
            'human_detected',
            'connected_to_agent',
            'voicemail',
          ],
        },
      };
      if (selectedCustomerId) baseFilter.customer_id = selectedCustomerId;
      let calls: ParallelCall[] = await api.entities.ParallelCall.filter<ParallelCall>(baseFilter);

      const effectiveTimeout =
        Number.isFinite(ringTimeout) && ringTimeout > 0 ? ringTimeout : 30;

      // Auto-cancel ringing calls qui depassent ringTimeout.
      const now = new Date();
      const timedOutIds = new Set<string>();
      const ringingTimedOut = calls.filter((call) => {
        if (!['ringing', 'dialing'].includes(call.call_status || '')) return false;
        const startedAt = new Date(call.started_at || call.created_date || '');
        return (now.getTime() - startedAt.getTime()) / 1000 > effectiveTimeout;
      });
      if (ringingTimedOut.length > 0) {
        for (const call of ringingTimedOut) {
          try {
            if (call.external_call_id) {
              await TwilioCalls.hangupCall(call.external_call_id);
            }
            await api.entities.ParallelCall.update(call.id, {
              call_status: 'completed',
              call_outcome: 'no_answer',
              // i18n-exempt: call note stored on the call record, not interface copy
              call_notes: `Pas de reponse apres ${effectiveTimeout}s`,
              ended_at: now.toISOString(),
            });
            timedOutIds.add(call.id);
          } catch (_e) {
            /* continue processing remaining calls */
          }
        }
        calls = calls.filter((c) => !timedOutIds.has(c.id));
      }

      // Conclure les appels coinces. La regle vit dans stuckCall.ts : elle
      // marquait « injoignable » toute conversation de plus de cinq minutes,
      // parce qu'un appel decroche reste en connected_to_agent du debut a la
      // fin. Les meilleurs appels etaient donc ceux qui se faisaient detruire.
      const stuckCalls = calls.filter((call) => isStuckCall(call, now));
      if (stuckCalls.length > 0) {
        for (const call of stuckCalls) {
          try {
            await api.entities.ParallelCall.update(call.id, {
              call_status: 'failed',
              call_outcome: 'not_reachable',
              // i18n-exempt: call note stored on the call record, not interface copy
              call_notes: 'Appel automatiquement termine (timeout)',
              ended_at: now.toISOString(),
            });
          } catch (_e) {
            /* swallow */
          }
        }
        return [];
      }

      return calls;
    },
    enabled: !!callerData?.id,
    // 2026-10-07: while a line rings or is held for screening, re-read every
    // second, even in a background tab: the contact card must open from the
    // call's state when the event stream is down (it opened only on
    // call:bridged, and a caller's stream stayed dead for an hour).
    refetchInterval: (query) => {
      const pending = (query.state.data || []).some((c) => ['dialing', 'ringing', 'ai_analyzing'].includes(c.call_status || ''));
      if (pending) return POLLING.activeCallPendingMs;
      return dialerRunState === 'running' || powerMode ? POLLING.activeCallSessionMs : POLLING.activeCallIdleMs;
    },
    refetchIntervalInBackground: true,
  });

  const value = useMemo<DialerDataContextValue>(
    () => ({
      selectedCustomerId,
      setSelectedCustomerId,
      prospectQueue,
      allCalls,
      activeCalls,
    }),
    [selectedCustomerId, setSelectedCustomerId, prospectQueue, allCalls, activeCalls],
  );

  return (
    <DialerDataContext.Provider value={value}>{children}</DialerDataContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useDialerData(): DialerDataContextValue {
  const ctx = useContext(DialerDataContext);
  if (!ctx) {
    throw new Error('useDialerData must be used within <DialerDataProvider>');
  }
  return ctx;
}
